import React, { useRef, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import AsyncCreatableSelect from 'react-select/async-creatable';
import { components } from 'react-select';
import { makeRecipientLoader } from 'helpers/debounce';
import { EmployeeAvatar } from './EmployeeBadge';

/**
 * RecipientSelect — reusable multi-select for email recipients (To/Cc).
 *
 * Renders each option as avatar (initials) + full name + title + email, and each
 * selected chip as a small avatar + name. It STORES/EMITS plain email strings so
 * callers keep a simple `string[]` value (e.g. form `to`/`cc` arrays).
 *
 * An address that belongs to no employee can still be typed in full — the create
 * option only appears for a syntactically valid email.
 *
 * Props:
 *   value        — string[] of selected emails
 *   onChange     — (emails: string[]) => void
 *   placeholder, isDisabled, employeeType('all'|'sales'|'csd'|'engineering'),
 *   hasError, autoFocus, minMenuWidth
 */

const isEmail = (str) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(str || '').trim());

const personName = (data) => data.fullName || data.name || data.label || data.email;

// Dropdown row: avatar + name + title + email. EmployeeAvatar is the app-wide
// avatar (photo when known, deterministic initials otherwise), so this picker
// looks identical to EmployeeRecipientSelect and PersonChip.
const Option = (props) => {
  const { data, isSelected } = props;
  const name = personName(data);
  return (
    <components.Option {...props}>
      <div className="tw-flex tw-items-center tw-gap-2.5 tw-py-0.5">
        <EmployeeAvatar name={name} avatarUrl={data.avatarUrl} size={32} fontSize={12} />
        <div className="tw-min-w-0 tw-flex-1">
          <div className={`tw-text-sm tw-font-semibold tw-truncate ${isSelected ? 'tw-text-white' : 'tw-text-slate-800'}`}>{name}</div>
          <div className={`tw-text-xs tw-truncate ${isSelected ? 'tw-text-primary-100' : 'tw-text-slate-400'}`}>
            {data.title ? `${data.title} · ` : ''}{data.email}
          </div>
        </div>
      </div>
    </components.Option>
  );
};
Option.propTypes = { data: PropTypes.object, isSelected: PropTypes.bool };

// Selected chip: avatar + the address, one line.
//
// The address alone, deliberately: a chip is a record of who will actually be
// mailed, and the field mixes people picked from the directory with prefilled and
// free-typed addresses. Showing the name whenever one happened to be known left
// rows of different heights saying different things about the same kind of
// recipient. The person's name stays one hover away, and in the dropdown.
const MultiValueLabel = (props) => {
  const { data } = props;
  const name = personName(data);
  const email = data.email || data.value || data.label;
  return (
    <components.MultiValueLabel {...props}>
      <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-min-w-0 tw-max-w-[230px]" title={name && name !== email ? `${name} <${email}>` : email}>
        <EmployeeAvatar name={name} avatarUrl={data.avatarUrl} size={20} fontSize={8} />
        <span className="tw-truncate tw-text-xs tw-font-medium tw-leading-tight">{email}</span>
      </span>
    </components.MultiValueLabel>
  );
};
MultiValueLabel.propTypes = { data: PropTypes.object };

const RecipientSelect = ({
  value = [],
  onChange,
  placeholder = 'Add recipients…',
  isDisabled = false,
  employeeType = 'all',
  hasError = false,
  autoFocus = false,
  knownOptions = [],
}) => {
  // One debounced loader + one option cache per instance (remembers names so
  // chips keep showing the full name even though we only store emails).
  const baseLoader = useRef(makeRecipientLoader(employeeType)).current;
  const cache = useRef({});
  const [, bumpRevision] = useState(0);

  // Every loaded option feeds the cache, not just the ones picked: a prefilled
  // address the backend supplied then resolves to its person as soon as the
  // employee list comes back, without the caller passing `knownOptions`.
  const loadOptions = (input, callback) => {
    baseLoader(input, (options) => {
      (options || []).forEach((option) => { if (option?.value) cache.current[option.value] = option; });
      bumpRevision((revision) => revision + 1);
      callback(options);
    });
  };

  // Prefilled recipients (e.g. backend defaults) arrive as email strings with no
  // cached option → chips would show the raw email. `knownOptions` supplies their
  // names so every chip renders consistently as name + avatar.
  const knownMap = useMemo(() => {
    const map = {};
    (knownOptions || []).forEach((o) => {
      const email = o.value || o.email;
      if (email) map[email] = { value: email, email, label: o.label || o.name || email, fullName: o.label || o.name };
    });
    return map;
  }, [knownOptions]);

  const selected = useMemo(
    () => (Array.isArray(value)
      ? value.map((email) => cache.current[email] || knownMap[email] || { value: email, label: email, email })
      : []),
    [value, knownMap]
  );

  const handleChange = (opts) => {
    const list = opts || [];
    list.forEach((o) => { if (o?.value) cache.current[o.value] = o; });
    onChange(list.map((o) => o.value));
  };

  const styles = {
    control: (base, state) => ({
      ...base,
      minHeight: 34,
      fontSize: 12,
      borderColor: hasError ? '#f87171' : state.isFocused ? '#818cf8' : '#e2e8f0',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(129,140,248,0.25)' : 'none',
      '&:hover': { borderColor: hasError ? '#f87171' : '#818cf8' },
    }),
    valueContainer: (base) => ({ ...base, padding: '2px 6px', gap: 4 }),
    multiValue: (base) => ({ ...base, background: '#eef2ff', borderRadius: 9999, padding: '1px 2px' }),
    multiValueRemove: (base) => ({ ...base, borderRadius: 9999, ':hover': { background: '#c7d2fe', color: '#3730a3' } }),
    // Above the compose dialogs that host this field (they sit at 999999) —
    // otherwise the portalled menu paints behind the modal and looks empty.
    menu: (base) => ({ ...base, zIndex: 1000001 }),
    menuPortal: (base) => ({ ...base, zIndex: 1000001 }),
    option: (base) => ({ ...base, padding: '6px 10px', backgroundColor: 'transparent', ':active': { backgroundColor: 'transparent' } }),
  };

  // menuPlacement auto: a To/CC row near the bottom of a drawer or modal
  // would otherwise open its menu under the footer.
  return (
    <AsyncCreatableSelect
      isMulti
      cacheOptions
      defaultOptions
      autoFocus={autoFocus}
      isDisabled={isDisabled}
      value={selected}
      onChange={handleChange}
      loadOptions={loadOptions}
      placeholder={placeholder}
      closeMenuOnSelect={false}
      classNamePrefix="react-select"
      components={{ Option, MultiValueLabel }}
      styles={styles}
      isValidNewOption={isEmail}
      formatCreateLabel={(input) => `Add "${input}"`}
      noOptionsMessage={() => 'Type to search employees'}
      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
      menuPosition="fixed"
      menuPlacement="auto"
      maxMenuHeight={260}
    />
  );
};

RecipientSelect.propTypes = {
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  isDisabled: PropTypes.bool,
  employeeType: PropTypes.string,
  hasError: PropTypes.bool,
  autoFocus: PropTypes.bool,
  knownOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    email: PropTypes.string,
    label: PropTypes.string,
    name: PropTypes.string,
  })),
};

export default RecipientSelect;
